import {
  ContextMenu,
  ContextMenuProps,
  GroupBox,
  IconName,
} from '@infotech-soe/erp-ui-kit';
import { TDatabaseId } from '@infotech-soe/metaschema';
import React, {
  FC,
  MouseEventHandler,
  SetStateAction,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import RTable from 'react-table';

import { CategoryOpenMode } from '../../../../../models/category/category';
import { WorkTableData } from '../../../../../models/work-table/WorkTableData';
import { useAppDispatch } from '../../../../../store/hooks';
import categoryActions from '../../../../../store/slices/category';
import { DictionaryEffectType } from '../../../../../store/slices/dictionary';
import workTable from '../../../../../store/slices/workTable';
import { useContextMenu } from '../../../../hooks/useContextMenu';
import { mapDaysTable } from '../mapState';
import useStyles from '../styles';

const messages = defineMessages({
  workDays: {
    id: 'core.worktable.workDays',
    defaultMessage: 'Work days',
  },
  edit: {
    id: 'core.category.commands.edit',
    defaultMessage: 'Edit',
  },
  delete: {
    id: 'core.category.commands.delete',
    defaultMessage: 'Delete',
  },
});

interface Props {
  workTableData: WorkTableData;
  onUpdateDays: (totalHours: number, totalDays: number) => void;
  year: number;
  month: number;
  tableId: TDatabaseId;
  hasContextMenu?: boolean;
}

export interface SelectedDay {
  rowId: number;
  colId: number;
}

export interface ContextValue {
  selected: SelectedDay[];
  setSelected: React.Dispatch<SetStateAction<SelectedDay[]>>;
  classes: Record<string, string>;
}

type ActionNames = 'edit' | 'delete';

export const Context = React.createContext<ContextValue | null>(null);

export const useWorkDaysContext = (): ContextValue => {
  const context = useContext(Context);
  if (context) return context;
  throw new Error('No WorkDays context specified');
};

const WorkDays: FC<Props> = ({
  workTableData,
  onUpdateDays,
  year,
  month,
  tableId,
  hasContextMenu = true,
}) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const classes = useStyles();
  const { data, columns, totalHours, totalDays } = mapDaysTable(
    workTableData,
    year,
    month,
    intl
  );
  const [selected, setSelected] = useState<SelectedDay[]>([]);
  const ctxValue: ContextValue = {
    selected,
    setSelected,
    classes,
  };

  const selectedDays = selected
    .filter(day => !!data[day.rowId][day.colId])
    .map(day => data[day.rowId][day.colId].dayNumber);

  const updateAction = (): void => {
    dispatch(
      categoryActions.openDisplayMode({
        category: 'SalaryDaysTypes',
        displayMode: 'main',
        ui: {
          mode: CategoryOpenMode.DICTIONARY,
          effect: {
            type: DictionaryEffectType.WORK_TABLE,
            selectedDays,
            tableId,
          },
          multiSelect: false,
        },
      })
    );
  };
  const deleteAction = (): void => {
    dispatch(workTable.deleteWorkDays(selectedDays));
  };

  const menu: ContextMenuProps<ActionNames>['options'] = [
    {
      type: 'listitem',
      value: 'edit',
      label: intl.formatMessage(messages.edit),
      icon: 'edit' as IconName,
      hotKey: 'F4',
    },
    {
      type: 'listitem',
      value: 'delete',
      label: intl.formatMessage(messages.delete),
      icon: 'delete' as IconName,
      hotKey: 'Delete',
    },
  ];
  const contextMenu = useContextMenu();
  const onContextMenuOpen: MouseEventHandler<HTMLDivElement> = event => {
    event.preventDefault();
    if (selectedDays.length) {
      const { clientX, clientY } = event;

      contextMenu.setPositionX(clientX);
      contextMenu.setPositionY(clientY);
      contextMenu.open();
    }
  };
  const onContextMenuClick = (item: ActionNames): void => {
    if (item === 'edit') {
      updateAction();
    } else if (item === 'delete') {
      deleteAction();
    }
    contextMenu.close();
  };
  const onContextMenuClose = (): void => {
    contextMenu.close();
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'F4') {
      updateAction();
    } else if (e.key === 'Delete') {
      deleteAction();
    }
  };

  useLayoutEffect(() => {
    onUpdateDays(totalHours, totalDays);
  }, [totalHours, totalDays, onUpdateDays]);

  return (
    <Context.Provider value={ctxValue}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        onKeyDown={handleKeyDown}
        onContextMenu={onContextMenuOpen}
        className="col"
      >
        <GroupBox label={intl.formatMessage(messages.workDays)}>
          <RTable
            data={data}
            columns={columns}
            minRows={0}
            sortable={false}
            resizable={false}
            showPagination={false}
            getTdProps={() => ({
              className: classes.dayCellContainer,
            })}
            getTbodyProps={() => ({
              className: classes.tBody,
            })}
            getTableProps={() => ({
              className: classes.table,
            })}
            getTheadProps={() => ({
              className: classes.tHead,
            })}
            getTheadThProps={() => ({
              className: classes.tHeadTh,
            })}
            getTrGroupProps={() => ({
              className: classes.trGroup,
            })}
          />
        </GroupBox>
      </div>
      {hasContextMenu && (
        <ContextMenu
          open={contextMenu.isOpen}
          options={menu}
          x={contextMenu.positionX}
          y={contextMenu.positionY}
          onClick={onContextMenuClick}
          onOpen={onContextMenuClose}
        />
      )}
    </Context.Provider>
  );
};

export default WorkDays;
